import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, AlertCircle, X as XIcon, CheckCircle, AlertTriangle } from 'lucide-react';
import { Button } from '@mui/material';

const BlastModal = ({ open, onClose, blastData, onConfirm }) => {
  if (!open) return null;

  const {
    recipientCount,
    smsCredits,
    costPerSMS,
    isBlastToAll,
    sender,
  } = blastData;

  const totalCost = (recipientCount * costPerSMS).toFixed(2);

  return (
    <AnimatePresence>
      <div className="overflow-y-auto fixed inset-0 z-50">
        <div className="flex justify-center items-center p-4 min-h-screen text-center">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 backdrop-blur-sm bg-black/30"
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="overflow-hidden relative w-full max-w-lg bg-white rounded-2xl shadow-xl"
          >
            {/* Close Button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-2 rounded-full transition-colors hover:bg-gray-100"
            >
              <XIcon className="w-5 h-5 text-gray-500" />
            </button>

            {/* Header */}
            <div className="p-6 text-center">
              <div className="flex justify-center items-center mx-auto mb-4 w-16 h-16 bg-orange-50 rounded-full">
                <AlertCircle className="w-8 h-8 text-orange-500" />
              </div>
              <h3 className="mb-2 text-2xl font-bold text-gray-900">
                Confirm SMS Blast
              </h3>
              <p className="text-gray-600">
                You're about to send an SMS blast to your selected audience
              </p>
            </div>

            {/* Content */}
            <div className="px-6 py-8 bg-gray-50">
              <div className="space-y-6">
                {/* Recipients Info */}
                <div className="flex justify-between items-center p-4 bg-white rounded-xl border border-gray-100">
                  <div className="flex gap-3 items-center">
                    <div className="p-2 bg-indigo-50 rounded-lg">
                      <Send className="w-5 h-5 text-indigo-600" />
                    </div>
                    <div className="text-left">
                      <div className="flex gap-2 items-center">
                        <p className="text-sm text-gray-600">From</p>
                        <span className="px-2 py-0.5 bg-indigo-50 rounded-full text-xs font-medium text-indigo-600">
                          {sender}
                        </span>
                      </div>
                      <p className="text-lg font-semibold text-gray-900">
                        {recipientCount.toLocaleString()} subscribers
                      </p>
                    </div>
                  </div>
                  {isBlastToAll && (
                    <span className="px-3 py-1 text-xs font-medium text-indigo-700 bg-indigo-50 rounded-full">
                      Full Blast
                    </span>
                  )}
                </div>

                {/* Cost Breakdown */}
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-4 bg-white rounded-xl border border-gray-100">
                    <div className="flex gap-3 items-center">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span className="text-gray-600">SMS Credits Available</span>
                    </div>
                    <span className="font-semibold text-gray-900">{smsCredits}</span>
                  </div>
                  <div className="flex justify-between items-center p-4 bg-white rounded-xl border border-gray-100">
                    <div className="flex gap-3 items-center">
                      <AlertTriangle className="w-5 h-5 text-orange-500" />
                      <span className="text-gray-600">Credits Required</span>
                    </div>
                    <span className="font-semibold text-gray-900">{recipientCount}</span>
                  </div>
                </div>

                {/* Total Cost */}
                <div className="p-4 bg-green-50 rounded-xl border border-green-100">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm font-medium text-green-700">Total Cost</p>
                      <p className="text-2xl font-bold text-green-700">
                        GHC {totalCost}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-green-600">Cost per SMS</p>
                      <p className="text-lg font-semibold text-green-700">
                        GHC {costPerSMS}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="px-6 py-4 bg-white border-t border-gray-100">
              <div className="flex flex-col gap-3 sm:flex-row sm:gap-4">
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={onClose}
                  sx={{
                    borderRadius: '12px',
                    borderColor: '#FF6C6C',
                    color: '#FF6C6C',
                    '&:hover': {
                      borderColor: '#ff5555',
                      backgroundColor: '#FFF5F5',
                    },
                  }}
                >
                  Cancel
                </Button>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={onConfirm}
                  startIcon={<Send className="w-4 h-4" />}
                  sx={{
                    borderRadius: '12px',
                    backgroundColor: '#FF6C6C',
                    '&:hover': {
                      backgroundColor: '#ff5555',
                    },
                  }}
                >
                  Confirm & Send
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </AnimatePresence>
  );
};

export default BlastModal;
